/**
 * App.tsx
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */
import React, { memo, useEffect } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import { AxiosResponse } from 'axios';
import { error } from 'components/_shared/snackbars/';
import { useAuth } from 'components/auth';
import { getUser } from 'components/auth/api';
import EnsureTranslation from 'components/language/components/EnsureTranslation';
import 'font-awesome/css/font-awesome.min.css';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useIsLatestVersion } from '../components/_shared/hooks/useIsLatestVersion';
import { Loader } from '../components/_shared/loader';
import { captureAppOrigin } from 'utils/appOrigin';
import BrandLandingPage from 'components/brand-landing/BrandLandingPage';
import AuthCallback from './AuthCallback';
import Dashboard from './Dashboard';
import Guest from './Guest';

// Capture appOrigin from URL before anything else runs
captureAppOrigin();

// Capture token from OAuth callback redirect (?token=...)
(() => {
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get('token');
  if (tokenFromUrl) {
    localStorage.setItem('token', tokenFromUrl);
    // Remove only the token; keep flags like claimBrandTrial=1 for post-login claim
    params.delete('token');
    const remaining = params.toString();
    window.history.replaceState(
      {},
      '',
      remaining
        ? `${window.location.pathname}?${remaining}`
        : window.location.pathname,
    );
  }
})();

function App() {
  const isLatestVersion = useIsLatestVersion();
  const intl = useIntl();
  const { user, token, setUser, setToken, isAuth } = useAuth();

  const { refetch } = useQuery('currentUser', getUser, {
    enabled: false,
    onSuccess: (response: AxiosResponse<any>) => {
      setUser(response.data.user);
      // If backend refreshed the token, update localStorage so future requests use it.
      // Never overwrite a local REACT_APP_DEV_TOKEN bypass with the DB user's QP JWT.
      const sessionToken = localStorage.getItem('token');
      const newToken = response.data.user?.accessToken;
      const isDevToken =
        !!process.env.REACT_APP_DEV_TOKEN &&
        sessionToken === process.env.REACT_APP_DEV_TOKEN;
      if (newToken && newToken !== sessionToken && !isDevToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
      }
    },
    onError: ({ statusCode }) => {
      setToken();

      // 401/403 are expected when the session is invalid — avoid noisy toasts in the loop
      if (statusCode !== 401 && statusCode !== 403) {
        error(EnsureTranslation({ intl, itemKey: 'general.errorLoginAgain' }));
      }
    },
  });

  useEffect(() => {
    if (!!token && !user) {
      refetch();
    }
  }, [token, user, refetch]);

  if (!isLatestVersion) {
    return (
      <>
        <Loader loading={true} fullPage={true} />
      </>
    );
  }

  return (
    <Router>
      <Switch>
        <Route path="/auth/:provider/callback" exact component={AuthCallback} />
        <Route path="/brand" exact component={BrandLandingPage} />
        <Route path="/" component={isAuth ? Dashboard : Guest} />
      </Switch>
    </Router>
  );
}

export default memo(App);
